import type { IRangeValues } from "pro-features/components/filtersPanel/src/RangeFilter";
import { RangeFilter } from "pro-features/components/filtersPanel/src/RangeFilter";
import {
    getVolumeSelectedText,
    INITIAL_RANGE_VALUES,
    isValidVolumeRangeValues,
    onContainerKeyDown,
    onDoneCallback,
    resetRangeValues,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctions";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import { i18nFilter } from "app/filters/i18nFilter";
import { useWebsiteKeywordsPageTableTopContext } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import React, { useState } from "react";
import { ChipDownContainer } from "@similarweb/ui-components";
import { useRankingDistributionTableTopContext } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/useRankingDistributionTableTopContext";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import noop from "lodash/noop";

const VOLUME_FILTER_WIDTH = 298;
const VOLUME_FILTER_BUTTON_TEXT_KEY = "keywords.table.filters.volume.button";
const VOLUME_FILTER_DISABLED_TOOLTIP_KEY = "keywords.table.filters.volume.disabled.tooltip";
const VOLUME_FILTER_SUPPORT_MAXIMUM_ROWS_AMOUNT = 50 * 1000;

// This is the representational component
export const VolumeFilter = ({
    onVolumeChange,
    tableFilters,
    disabled,
    tooltipText = null,
    onOpenVolume = noop,
    filterTextKey = VOLUME_FILTER_BUTTON_TEXT_KEY,
}: any) => {
    const { volumeFromValue, volumeToValue } = tableFilters;
    const volumeChipDownRef = React.useRef(undefined);
    const i18n = i18nFilter();
    const [volumeValue, setVolumeValue] = useState({
        fromValue: volumeFromValue,
        toValue: volumeToValue,
    });

    const onCpcValueChange = (value) => {
        setVolumeValue(value);
    };

    if (disabled && (volumeFromValue || volumeToValue)) {
        onVolumeChange(INITIAL_RANGE_VALUES);
        proNavigator.applyUpdateParams({
            volumeFromValue: undefined,
            volumeToValue: undefined,
        });
    }
    const onToggle = (isOpen: boolean, isOutsideClick: boolean) => {
        if (isOutsideClick) {
            onDoneCallback(onVolumeChange(volumeValue), volumeChipDownRef);
        } else if (isOpen) {
            onOpenVolume();
        }
    };
    const onCloseItem = () => {
        resetRangeValues(onVolumeChange)();
        setVolumeValue({ fromValue: String(), toValue: String() });
    };
    const onClearCallback = () => {
        setVolumeValue({ fromValue: String(), toValue: String() });
    };

    const doneSelected = (rangeValues: IRangeValues) => {
        onDoneCallback(onVolumeChange, volumeChipDownRef)(rangeValues);
    };

    return (
        <PlainTooltip
            enabled={disabled}
            text={VOLUME_FILTER_DISABLED_TOOLTIP_KEY}
        >
            <div onKeyDown={onContainerKeyDown(volumeChipDownRef)}>
                <ChipDownContainer
                    disabled={disabled}
                    closeOnItemClick={false}
                    ref={volumeChipDownRef}
                    width={VOLUME_FILTER_WIDTH}
                    selectedText={getVolumeSelectedText(volumeValue)}
                    buttonText={i18n(filterTextKey)}
                    onCloseItem={onCloseItem}
                    tooltipText={tooltipText}
                    onToggle={onToggle}
                    noResultsMessage={i18n("dropdown.no.results")}
                >
                    {[
                        <RangeFilter
                            key="range-filter"
                            onDoneCallback={doneSelected}
                            initialFromValue={volumeValue.fromValue}
                            initialToValue={volumeValue.toValue}
                            isValidRangeValues={isValidVolumeRangeValues}
                            onValueChange={onCpcValueChange}
                            onClearCallback={onClearCallback}
                        />,
                    ]}
                </ChipDownContainer>
            </div>
        </PlainTooltip>
    );
};
// This are the "connectors" that connect the certain context to the representational component
export const VolumeFilterForWebsiteKeywords = () => {
    const { onVolumeChange, tableFilters, tableData, onOpenVolume, onOpenCpc } =
        useWebsiteKeywordsPageTableTopContext();
    return (
        <VolumeFilter
            disabled={tableData?.TotalCount > VOLUME_FILTER_SUPPORT_MAXIMUM_ROWS_AMOUNT}
            tableFilters={tableFilters}
            tableData={tableData}
            onVolumeChange={onVolumeChange}
            onOpenVolume={onOpenVolume}
            onOpenCpc={onOpenCpc}
        />
    );
};

export const VolumeFilterForRankingDistribution = () => {
    const { onVolumeChange, tableFilters, tableData } = useRankingDistributionTableTopContext();
    return (
        <VolumeFilter
            disabled={false}
            tableFilters={tableFilters}
            tableData={tableData}
            onVolumeChange={onVolumeChange}
        />
    );
};
